interface UsageRatioBarProps {
  /** 百分比数值，如 50 表示 50% */
  ratio: number;
  className?: string;
  /**
   * 进度条轨宽（固定像素，避免表格/弹性布局下同等比例一长一短）
   * 默认 96（等同 w-24）
   */
  barWidthPx?: number;
  /** @deprecated 请使用 barWidthPx；传入时仍会强制固定宽度 */
  barClassName?: string;
}

/** 统一默认轨宽（px） */
export const USAGE_RATIO_BAR_WIDTH_PX = 96;

function getUsageRatioTone(ratio: number) {
  // 按展示的两位小数：>100% 危险色；=100% 灰色；95%–100% 紫色
  const shown = Number(ratio.toFixed(2));
  if (shown > 100) return { bar: 'bg-red-500', text: 'text-red-600' };
  if (shown === 100) return { bar: 'bg-foreground-400', text: 'text-foreground-500' };
  if (shown >= 95) return { bar: 'bg-purple-500', text: 'text-purple-600' };
  // <50 主题色；50–80 蓝色；80–95 警告色
  if (ratio > 80) return { bar: 'bg-amber-500', text: 'text-amber-600' };
  if (ratio >= 50) return { bar: 'bg-blue-500', text: 'text-blue-600' };
  return { bar: 'bg-primary-500', text: 'text-primary-600' };
}

function resolveBarWidthPx(barWidthPx?: number, barClassName?: string): number {
  if (typeof barWidthPx === 'number' && Number.isFinite(barWidthPx) && barWidthPx > 0) {
    return barWidthPx;
  }
  // 兼容历史 Tailwind 宽度类
  if (barClassName?.includes('w-14')) return 56;
  if (barClassName?.includes('w-16')) return 64;
  if (barClassName?.includes('w-20')) return 80;
  if (barClassName?.includes('w-28')) return 112;
  if (barClassName?.includes('w-32')) return 128;
  return USAGE_RATIO_BAR_WIDTH_PX;
}

export default function UsageRatioBar({
  ratio,
  className = '',
  barWidthPx,
  barClassName,
}: UsageRatioBarProps) {
  const value = Number.isFinite(ratio) ? ratio : 0;
  const fillPercent = Math.max(0, Math.min(100, value));
  const tone = getUsageRatioTone(value);
  const trackWidth = resolveBarWidthPx(barWidthPx, barClassName);

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <div
        className="h-1.5 rounded-full bg-background-200 overflow-hidden shrink-0 grow-0"
        style={{ width: trackWidth, minWidth: trackWidth, maxWidth: trackWidth }}
        role="progressbar"
        aria-valuenow={Math.round(fillPercent)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-[width] ${tone.bar}`}
          style={{ width: `${fillPercent}%` }}
        />
      </div>
      <span
        className={`text-xs font-semibold tabular-nums whitespace-nowrap shrink-0 w-[4.5rem] ${tone.text}`}
      >
        {value.toFixed(2)}%
      </span>
    </div>
  );
}
